import { useEffect, useState } from "react";
import {
    KeyRound, Bot, Image as ImageIcon, CheckCircle2, AlertTriangle,
    Loader2, RefreshCw, Save, Zap, Info, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CHAT_MODELS = ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini", "gpt-4.1", "gpt-5-mini"];
const OPENAI_IMAGE_MODELS = ["gpt-image-1"];
const GEMINI_IMAGE_MODELS = ["gemini-2.5-flash-image"];

export default function AdminIntegrations() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState("");

    // form state
    const [openaiKey, setOpenaiKey] = useState("");
    const [chatModel, setChatModel] = useState("gpt-4o-mini");
    const [imageProvider, setImageProvider] = useState("openai");
    const [openaiImageModel, setOpenaiImageModel] = useState("gpt-image-1");
    const [geminiKey, setGeminiKey] = useState("");
    const [geminiImageModel, setGeminiImageModel] = useState("gemini-2.5-flash-image");

    // Stripe (payments) state
    const [payStatus, setPayStatus] = useState(null);
    const [stripeSecret, setStripeSecret] = useState("");
    const [stripePublishable, setStripePublishable] = useState("");
    const [stripeWebhook, setStripeWebhook] = useState("");
    const [savingPay, setSavingPay] = useState(false);
    const [testingPay, setTestingPay] = useState(false);

    async function fetchPayStatus() {
        try {
            const { data } = await api.get("/admin/integrations/payments");
            setPayStatus(data);
            setStripePublishable(data.publishable_key || "");
        } catch (e) {
            /* silent */
        }
    }

    async function savePay() {
        setSavingPay(true);
        try {
            const payload = { stripe_publishable_key: stripePublishable };
            if (stripeSecret.trim()) payload.stripe_secret_key = stripeSecret.trim();
            if (stripeWebhook.trim()) payload.stripe_webhook_secret = stripeWebhook.trim();
            const { data } = await api.put("/admin/integrations/payments", payload);
            setPayStatus(data);
            setStripeSecret("");
            setStripeWebhook("");
            toast.success("Definições de pagamentos guardadas.");
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setSavingPay(false);
        }
    }

    async function testPay() {
        setTestingPay(true);
        try {
            const { data } = await api.post("/admin/integrations/payments/test", {});
            toast.success(`Stripe OK (${data.mode} · ${data.country || "?"})`);
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setTestingPay(false);
        }
    }

    async function fetchStatus() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/integrations/ai");
            setStatus(data);
            setChatModel(data.chat_model || "gpt-4o-mini");
            setImageProvider(data.image_provider || "openai");
            setOpenaiImageModel(data.openai_image_model || "gpt-image-1");
            setGeminiImageModel(data.gemini_image_model || "gemini-2.5-flash-image");
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchStatus();
        fetchPayStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function save() {
        setSaving(true);
        try {
            const payload = {
                chat_model: chatModel,
                image_provider: imageProvider,
                openai_image_model: openaiImageModel,
                gemini_image_model: geminiImageModel,
            };
            if (openaiKey.trim()) payload.openai_api_key = openaiKey.trim();
            if (geminiKey.trim()) payload.gemini_api_key = geminiKey.trim();
            const { data } = await api.put("/admin/integrations/ai", payload);
            setStatus(data);
            setOpenaiKey("");
            setGeminiKey("");
            toast.success("Definições de IA guardadas.");
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    }

    async function runTest(kind) {
        setTesting(kind);
        try {
            const { data } = await api.post("/admin/integrations/ai/test", { kind });
            toast.success(
                kind === "chat"
                    ? `Chat OK (${data.model})${data.sample ? ` — "${data.sample}"` : ""}`
                    : `Imagem OK (${data.provider} · ${data.model})`
            );
        } catch (e) {
            toast.error("Falhou: " + formatApiError(e?.response?.data?.detail || e.message));
        } finally {
            setTesting("");
        }
    }

    return (
        <div className="space-y-4" data-testid="admin-integrations">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-heading text-xl lg:text-2xl font-extrabold text-primary leading-tight">Integrações / IA</h1>
                    <p className="text-muted-foreground mt-1">
                        Configura aqui as chaves das APIs de IA. Guardadas de forma segura e usadas em
                        produção — sem depender de nenhum serviço externo de terceiros.
                    </p>
                </div>
                <button onClick={fetchStatus} disabled={loading}
                    className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-muted transition disabled:opacity-50">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Recarregar
                </button>
            </header>

            {/* Status banner */}
            <section className="grid gap-4 sm:grid-cols-3">
                <StatusCard
                    icon={Bot} label="Assistente (chat)"
                    ok={status?.chat_configured}
                    okText={`Ativo · ${status?.chat_model || ""}`}
                    offText="Sem chave OpenAI"
                />
                <StatusCard
                    icon={ImageIcon} label="Geração de imagem (branding)"
                    ok={status?.image_configured}
                    okText={`Ativo · ${status?.image_provider || ""}`}
                    offText="Sem chave do provedor de imagem"
                />
                <StatusCard
                    icon={CreditCard} label="Pagamentos (Stripe)"
                    ok={payStatus?.configured}
                    okText={`Ativo · modo ${payStatus?.mode || ""}`}
                    offText="Sem chave Stripe"
                />
            </section>

            {/* OpenAI */}
            <section className="rounded-2xl border border-border bg-white p-6 lg:p-8 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/5 text-primary">
                        <KeyRound className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="font-heading text-xl font-bold text-primary">OpenAI</h2>
                        <p className="text-sm text-muted-foreground">Usado no assistente de chat (e na imagem, se o provedor for OpenAI).</p>
                    </div>
                </div>

                <Field label="OpenAI API Key" hint={status?.openai_key_hint}>
                    <input
                        type="password" autoComplete="off" value={openaiKey}
                        onChange={(e) => setOpenaiKey(e.target.value)}
                        placeholder={status?.openai_key_hint ? `Definida (${status.openai_key_hint}) — escreve para substituir` : "sk-..."}
                        data-testid="openai-key"
                        className="w-full rounded-md border border-border bg-white px-4 py-2.5 text-sm text-primary font-mono focus:outline-none focus:ring-2 focus:ring-secondary"
                    />
                </Field>

                <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Modelo de chat">
                        <SelectOrText value={chatModel} onChange={setChatModel} options={CHAT_MODELS} testid="chat-model" />
                    </Field>
                    <div className="flex items-end">
                        <button onClick={() => runTest("chat")} disabled={testing === "chat"}
                            data-testid="test-chat"
                            className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2.5 text-sm font-semibold text-primary hover:bg-muted transition disabled:opacity-50">
                            {testing === "chat" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                            Testar chat
                        </button>
                    </div>
                </div>
            </section>

            {/* Image generation */}
            <section className="rounded-2xl border border-border bg-white p-6 lg:p-8 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary/15 text-primary">
                        <ImageIcon className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="font-heading text-xl font-bold text-primary">Geração de imagem</h2>
                        <p className="text-sm text-muted-foreground">Proposta de branding (logo + carrinho → nova imagem).</p>
                    </div>
                </div>

                <Field label="Provedor">
                    <div className="flex gap-3 flex-wrap">
                        {["openai", "gemini"].map((p) => (
                            <button
                                key={p} type="button" onClick={() => setImageProvider(p)}
                                data-testid={`image-provider-${p}`}
                                className={`rounded-full px-5 py-2 text-sm font-semibold border transition ${imageProvider === p ? "bg-primary text-white border-primary" : "bg-white text-primary border-border hover:bg-muted"}`}
                            >
                                {p === "openai" ? "OpenAI · gpt-image-1" : "Google Gemini · Nano Banana"}
                            </button>
                        ))}
                    </div>
                </Field>

                {imageProvider === "openai" ? (
                    <Field label="Modelo de imagem (OpenAI)">
                        <SelectOrText value={openaiImageModel} onChange={setOpenaiImageModel} options={OPENAI_IMAGE_MODELS} testid="openai-image-model" />
                    </Field>
                ) : (
                    <>
                        <Field label="Gemini API Key" hint={status?.gemini_key_hint}>
                            <input
                                type="password" autoComplete="off" value={geminiKey}
                                onChange={(e) => setGeminiKey(e.target.value)}
                                placeholder={status?.gemini_key_hint ? `Definida (${status.gemini_key_hint}) — escreve para substituir` : "AIza..."}
                                data-testid="gemini-key"
                                className="w-full rounded-md border border-border bg-white px-4 py-2.5 text-sm text-primary font-mono focus:outline-none focus:ring-2 focus:ring-secondary"
                            />
                        </Field>
                        <Field label="Modelo de imagem (Gemini)">
                            <SelectOrText value={geminiImageModel} onChange={setGeminiImageModel} options={GEMINI_IMAGE_MODELS} testid="gemini-image-model" />
                        </Field>
                    </>
                )}

                <button onClick={() => runTest("image")} disabled={testing === "image"}
                    data-testid="test-image"
                    className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2.5 text-sm font-semibold text-primary hover:bg-muted transition disabled:opacity-50">
                    {testing === "image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                    Testar ligação de imagem
                </button>
            </section>

            {/* Save */}
            <div className="flex items-center gap-4">
                <button onClick={save} disabled={saving} data-testid="save-integrations"
                    className="inline-flex items-center gap-2 rounded-md bg-secondary px-6 py-3 text-sm font-bold text-primary shadow-sm hover:-translate-y-0.5 transition disabled:opacity-50 disabled:translate-y-0">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "A guardar…" : "Guardar definições"}
                </button>
                <p className="text-xs text-muted-foreground">As chaves nunca são mostradas por completo. Deixa em branco para manter a atual.</p>
            </div>

            {/* Info */}
            <section className="rounded-2xl border border-border bg-muted/40 p-6 lg:p-8">
                <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground space-y-2">
                        <p>Obtém a chave OpenAI em <a className="underline" href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a>.</p>
                        <p>Obtém a chave Gemini (Google AI Studio) em <a className="underline" href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer">aistudio.google.com/app/apikey</a>.</p>
                        <p>Alternativamente, podes definir as mesmas chaves nas variáveis de ambiente do backend (<code className="text-xs">OPENAI_API_KEY</code>, <code className="text-xs">GEMINI_API_KEY</code>). O painel tem prioridade.</p>
                    </div>
                </div>
            </section>
        </div>
    );
}

function StatusCard({ icon: Icon, label, ok, okText, offText }) {
    return (
        <div className="rounded-2xl border border-border bg-white p-5 flex items-start gap-4">
            <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${ok ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
                <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-primary">
                    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                    <span className="truncate">{ok ? okText : offText}</span>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">
                {label}{hint ? <span className="ml-2 font-mono normal-case tracking-normal text-emerald-600">definida {hint}</span> : null}
            </label>
            {children}
        </div>
    );
}

function SelectOrText({ value, onChange, options, testid }) {
    return (
        <>
            <input
                list={`${testid}-list`} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full rounded-md border border-border bg-white px-4 py-2.5 text-sm text-primary font-mono focus:outline-none focus:ring-2 focus:ring-secondary"
            />
            <datalist id={`${testid}-list`}>
                {options.map((o) => <option key={o} value={o} />)}
            </datalist>
        </>
    );
}
